import React, { useState, useEffect, useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { BellRing, ChevronDown, ChevronUp } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { useAuth } from '../context/AuthContext';
import { nameMismatch } from '../utils/competitorMatchSanity';

// Competitor discount watch (High5 price-by-seller page). Data: /api/competitor-intel/discount-trend
//  - alerts: a seller's latest crawl cut prices on many products of a brand (or a sale ended)
//  - chart: per day, share of a brand's products sold with a struck-through price, per seller;
//    sellers that never show an old price (Beymen, Yalıspor) are only visible in
//    the "price cuts" view

const API_BASE = 'https://api.glowytics.com';
const COLORS = ['#dc2626', '#2563eb', '#16a34a', '#d97706', '#7c3aed', '#0891b2', '#db2777', '#4b5563'];
const fmt = (v) =>
  v == null ? '—' : `₺${Number(v).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
const pct = (v, d = 0) => (v == null ? '—' : `${(v * 100).toFixed(d)}%`);
const BRAND_LABEL = { on: 'On', asics: 'ASICS', hoka: 'HOKA', brooks: 'Brooks', salomon: 'Salomon', saucony: 'Saucony',
  skechers: 'Skechers', 'new balance': 'New Balance' };
const day = (d) => new Date(`${d}T00:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });

export default function CompetitorDiscountWatch({ brandKey }) {
  const { selectedBrand } = useAuth();
  const [data, setData] = useState(null);
  const [metric, setMetric] = useState('share'); // 'share' | 'drops'
  const [open, setOpen] = useState(null);

  useEffect(() => {
    let alive = true;
    fetch(`${API_BASE}/api/competitor-intel/discount-trend`, {
      credentials: 'include',
      headers: { 'X-Brand-ID': selectedBrand?.id || localStorage.getItem('currentBrandId') || '' },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (alive) setData(d); })
      .catch(() => {});
    return () => { alive = false; };
  }, [selectedBrand?.id]);

  const OUR = data?.ourSite || 'High5';

  const { rows, sites } = useMemo(() => {
    const s = (data?.series || []).filter((r) => r.b === brandKey);
    const showsOld = new Set(s.filter((r) => r.disc > 0).map((r) => r.site));
    const siteList = [...new Set(s.map((r) => r.site))]
      .filter((x) => metric === 'drops' || showsOld.has(x) || x === OUR)
      .sort((a, b) => (a === OUR) - (b === OUR) || a.localeCompare(b));
    const byDay = new Map();
    for (const r of s) {
      if (!byDay.has(r.d)) byDay.set(r.d, { d: r.d });
      byDay.get(r.d)[r.site] = metric === 'share' ? (r.n ? (r.disc / r.n) * 100 : null) : r.drops;
    }
    return { rows: [...byDay.values()].sort((a, b) => a.d.localeCompare(b.d)), sites: siteList };
  }, [data, brandKey, metric, OUR]);

  if (!data) return null;
  const alerts = data.alerts || [];

  return (
    <Card className="rounded-2xl overflow-hidden shadow-sm border border-gray-100">
      <div className="px-5 py-3 bg-gray-50 border-b border-gray-100 flex flex-wrap items-center gap-3">
        <BellRing className="w-4 h-4 text-gray-500" />
        <span className="text-sm font-semibold text-gray-700">Competitor discounts</span>
        <span className="text-xs text-gray-400">checked on every daily crawl · alert when a seller cuts prices on 5+ products (5%+ of the brand) at once</span>
      </div>

      {/* Alerts (all brands) */}
      <div className="p-4 space-y-3">
        {alerts.length === 0 && (
          <div className="text-sm text-gray-500">No new discount at any seller on the latest crawl.</div>
        )}
        {alerts.map((a, i) => {
          const key = `${a.site}|${a.brand}`;
          const ended = a.kind === 'ended';
          // a match whose names disagree (gender / Trail / type) is not "our product"
          const prods = a.products.map((p) => (p.our_price != null && nameMismatch(p.our_name, p.name)
            ? { ...p, our_name: null, our_price: null, our_url: null } : p));
          const ours = prods.filter((p) => p.our_price != null);
          return (
            <div key={key} className={`rounded-xl border ${ended ? 'border-amber-200 bg-amber-50' : 'border-red-200 bg-red-50'}`}>
              <button type="button" onClick={() => setOpen(open === key ? null : key)}
                className="w-full text-left px-4 py-3 flex items-start gap-3">
                <span className={`mt-0.5 shrink-0 w-2.5 h-2.5 rounded-full ${ended ? 'bg-amber-500' : 'bg-red-600 animate-pulse'}`} />
                <div className="flex-1 text-sm">
                  <div className={`font-semibold ${ended ? 'text-amber-800' : 'text-red-800'}`}>
                    {a.site} · {BRAND_LABEL[a.brand] || a.brand}: {ended ? 'sale ended' : 'discount started'} on {day(a.date)}
                  </div>
                  <div className="text-gray-700 mt-0.5">
                    {ended
                      ? `${a.rises} products went back up in price.`
                      : `${a.drops} products cut in price (median ${pct(a.medDrop)}) vs ${day(a.prevDate)}.`}
                    {' '}Products with a struck-through price: {a.prevDisc} → <b>{a.disc}</b> of {a.n}.
                    {ours.length > 0 && <> {ours.length} of them are products we sell too.</>}
                  </div>
                </div>
                {open === key ? <ChevronUp className="w-4 h-4 text-gray-500 mt-1" /> : <ChevronDown className="w-4 h-4 text-gray-500 mt-1" />}
              </button>
              {open === key && (
                <div className="border-t border-white/60 bg-white overflow-auto max-h-[480px] rounded-b-xl">
                  <table className="min-w-full text-sm">
                    <thead className="bg-white sticky top-0 border-b border-gray-100">
                      <tr className="text-gray-600">
                        <th className="text-left px-4 py-2 font-semibold">{a.site} product</th>
                        <th className="text-right px-4 py-2 font-semibold">Before</th>
                        <th className="text-right px-4 py-2 font-semibold">Now</th>
                        <th className="text-right px-4 py-2 font-semibold">Change</th>
                        <th className="text-left px-4 py-2 font-semibold">Our product</th>
                        <th className="text-right px-4 py-2 font-semibold">Our price</th>
                        <th className="text-right px-4 py-2 font-semibold">Us vs them</th>
                      </tr>
                    </thead>
                    <tbody>
                      {prods.map((p) => {
                        const ch = p.price / p.old_price - 1;
                        const gap = p.our_price != null ? p.our_price / p.price - 1 : null;
                        return (
                          <tr key={p.url} className="border-b border-gray-50 hover:bg-gray-50">
                            <td className="px-4 py-2">
                              <div className="flex items-center gap-3">
                                {p.image ? <img src={p.image} alt="" loading="lazy" className="w-10 h-10 object-cover rounded-lg border border-gray-200" />
                                  : <div className="w-10 h-10 rounded-lg bg-gray-100" />}
                                <a href={p.url} target="_blank" rel="noreferrer"
                                  className="text-gray-900 underline decoration-gray-300 underline-offset-2 hover:decoration-gray-600 min-w-[200px]">{p.name}</a>
                              </div>
                            </td>
                            <td className="px-4 py-2 text-right tabular-nums text-gray-400 line-through">{fmt(p.old_price)}</td>
                            <td className="px-4 py-2 text-right tabular-nums font-semibold">{fmt(p.price)}</td>
                            <td className={`px-4 py-2 text-right tabular-nums ${ch < 0 ? 'text-red-600' : 'text-amber-600'}`}>{ch > 0 ? '+' : ''}{(ch * 100).toFixed(0)}%</td>
                            <td className="px-4 py-2">
                              {p.our_url ? (
                                <a href={p.our_url} target="_blank" rel="noreferrer"
                                  className="text-gray-900 underline decoration-gray-300 underline-offset-2 hover:text-indigo-700">{p.our_name}</a>
                              ) : <span className="text-gray-300">not matched</span>}
                            </td>
                            <td className="px-4 py-2 text-right tabular-nums text-indigo-700">{fmt(p.our_price)}</td>
                            <td className={`px-4 py-2 text-right tabular-nums font-medium ${gap == null ? 'text-gray-300' : gap > 0.005 ? 'text-red-600' : 'text-emerald-600'}`}>
                              {gap == null ? '—' : `${gap > 0 ? '+' : ''}${(gap * 100).toFixed(1)}%`}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Daily chart for the selected brand */}
      <div className="px-5 pb-5">
        <div className="flex flex-wrap items-center gap-3 mb-2">
          <span className="text-sm font-semibold text-gray-800">{BRAND_LABEL[brandKey] || brandKey}, day by day</span>
          <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden bg-white">
            {[{ k: 'share', label: 'Struck-through price share' }, { k: 'drops', label: 'Price cuts vs previous day' }].map((b, i) => (
              <button key={b.k} type="button" onClick={() => setMetric(b.k)}
                className={`px-3 h-8 text-xs ${i ? 'border-l border-gray-200' : ''} ${metric === b.k ? 'bg-gray-900 text-white' : 'text-gray-700 hover:bg-gray-50'}`}>
                {b.label}
              </button>
            ))}
          </div>
          <span className="text-xs text-gray-400">
            {metric === 'share'
              ? '% of the seller’s products of this brand shown with an old price struck through. Beymen and Yalıspor never show an old price.'
              : 'Number of products whose price fell 3%+ since the previous crawl. Gaps = no crawl that day.'}
          </span>
        </div>
        <div style={{ width: '100%', height: 280 }}>
          <ResponsiveContainer>
            <LineChart data={rows} margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
              <CartesianGrid stroke="#f1f5f9" vertical={false} />
              <XAxis dataKey="d" tickFormatter={day} tick={{ fontSize: 11, fill: '#6b7280' }} minTickGap={24} />
              <YAxis tick={{ fontSize: 11, fill: '#6b7280' }} width={40}
                tickFormatter={(v) => (metric === 'share' ? `${v}%` : v)} allowDecimals={metric === 'share'} />
              <Tooltip labelFormatter={day}
                formatter={(v, n) => [metric === 'share' ? `${Number(v).toFixed(1)}%` : v, n]} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              {sites.map((s, i) => (
                <Line key={s} type="monotone" dataKey={s} connectNulls dot={false} isAnimationActive={false} strokeWidth={s === OUR ? 2.5 : 2}
                  stroke={s === OUR ? '#4338ca' : COLORS[i % COLORS.length]} strokeDasharray={s === OUR ? '6 4' : undefined}
                  name={s === OUR ? `${OUR} (Us)` : s} />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </Card>
  );
}
